/**
 * Match statistics, derived from what the world already reports.
 *
 * `MatchStats` (../game/cosmetics) is the contract every challenge and every
 * certification is written against, and its comment is explicit about why:
 * there must be exactly one definition of "a save". This file is that
 * definition. Nothing else in the tree may decide what a save is, and nothing
 * here may invent a statistic the simulation cannot actually produce.
 *
 * ===========================================================================
 * WHAT COUNTS, EXACTLY
 * ===========================================================================
 *
 * TOUCH — one entry in `WorldStepEvents.ballTouches` for that car. The world
 *   already resolves at most one car-ball contact per car per tick, so this is
 *   a count of contacts and not of ticks spent dribbling.
 *
 * ON TARGET — the geometric predicate the next three definitions are built on.
 *   A ball is on target for a net when its BALLISTIC path (gravity, no bounces,
 *   no further contacts) crosses that goal's plane inside the mouth, within
 *   `ON_TARGET_HORIZON` seconds. The mouth is inset by the ball's radius on
 *   both axes, because the ball has to FIT through it rather than merely aim at
 *   its centre, and the height is clamped at the floor: a ball rolling flat is
 *   not falling, and an unclamped parabola would rule out every ground shot
 *   taken from more than half a second out.
 *
 * SHOT — a car-ball contact after which the ball leaves at `SHOT_MIN_SPEED` or
 *   more and is on target for the OPPONENTS' net. Credited to the last car to
 *   touch the ball on that tick, because that is the car whose contact produced
 *   the velocity being judged. A ball put on target towards your own net is
 *   never a shot; see own goals below.
 *
 * SAVE — credited to a defender who touches the ball while an opponent's shot
 *   is live and, by that touch, takes the ball off target for their own net. A
 *   "live shot" is the threat opened by the SHOT above and closed by a save, a
 *   goal, a kickoff, or the ball drifting off target on its own (a miss). Three
 *   consequences worth stating: clearing a ball that was never on target is not
 *   a save; blocking your own team-mate's shot is not a save; and a shot that
 *   misses by itself credits nobody, which is right — nothing was saved.
 *
 * ASSIST — on a goal credited to S, the most recent car OTHER than S to have
 *   touched the ball, provided that car is on S's team and touched within
 *   `ASSIST_WINDOW_TICKS`. An opponent touching in between is itself the most
 *   recent other car, so it cancels the assist rather than being skipped over.
 *   Own goals never assist.
 *
 * AERIAL GOAL — the scoring touch was made with the car airborne AND its centre
 *   at or above `AERIAL_MIN_Z`. Both halves are needed: `onGround` alone is
 *   false for the first frame of every dodge on the floor.
 *
 * BACKWARDS GOAL — at the scoring touch, the car's forward axis pointed away
 *   from the direction the ball left in (a negative dot product). That is what
 *   "scored driving backwards" means when the car may also be upside down on a
 *   wall, where a velocity-sign test would be meaningless.
 *
 * LONG GOAL — the scoring touch happened at least `LONG_GOAL_DISTANCE` from the
 *   goal line it went in at, i.e. from the scorer's own half or beyond.
 *
 * OWN GOAL — the world reports `scorer: null` when the last toucher defends the
 *   net the ball entered. That last toucher is who it is charged to.
 *
 * MVP — the highest match score on the WINNING team, as in the real game; a
 *   drawn match has no MVP. The score weights are `MVP_SCORE` below.
 *
 * ===========================================================================
 * NO WORLD IMPORT
 * ===========================================================================
 *
 * `observe` takes a structural `StatTickEvents`, which `WorldStepEvents`
 * satisfies without either file knowing about the other. src/game is below
 * src/sim in the dependency order — src/sim/world.ts imports ./match from here
 * — and a type-only import back up would still be a layering inversion for a
 * reader. It also means a test can script a scenario by hand, which is the only
 * honest way to assert "this produced exactly two saves".
 */

import { BALL_RADIUS, GRAVITY } from '../physics/constants';
import { GOAL } from '../physics/arena-data';
import { dist, dot, forward, type Vec3 } from '../physics/math';
import type { CarState } from '../physics/car-state';
import type { BallState } from '../physics/ball';
import type { GoalEvent } from './match';
import type { MatchStats } from './cosmetics';

// ---------------------------------------------------------------------------
// Thresholds
// ---------------------------------------------------------------------------

/** A ball more than this far from the goal in time is a pass, not a shot. */
export const ON_TARGET_HORIZON = 4;

/** Minimum outgoing ball speed for a contact to be a shot, uu/s. */
export const SHOT_MIN_SPEED = 500;

/** How long a pass stays creditable as an assist. 5 s, as in the real game. */
export const ASSIST_WINDOW_TICKS = 600;

/** Car centre height at which a goal is an aerial one, uu. Well above a dodge. */
export const AERIAL_MIN_Z = 500;

/** Distance from the goal line that makes a goal a long one, uu — half a pitch. */
export const LONG_GOAL_DISTANCE = 5120;

/**
 * Match-score weights. Rocket League's own: a goal is worth five shots, and a
 * save is worth as much as an assist. Only used to pick the MVP.
 */
export const MVP_SCORE = {
  goal: 100,
  assist: 50,
  save: 50,
  shot: 20,
  demolition: 20,
} as const;

// ---------------------------------------------------------------------------
// The structural view of a world tick
// ---------------------------------------------------------------------------

/** One car-ball contact, as the world reports it. */
export interface StatBallTouch {
  carIndex: number;
  team: 0 | 1;
}

/** One demolition, as the world reports it: the two cars, not their indices. */
export interface StatDemolition {
  attacker: CarState;
  victim: CarState;
}

/** One boost pad collected. */
export interface StatBoostPickup {
  car: CarState;
}

/**
 * Everything `observe` reads out of a tick. `WorldStepEvents` from
 * src/sim/world.ts is assignable to this; so is a hand-built literal, which is
 * what the tests use.
 */
export interface StatTickEvents {
  frozen: boolean;
  kickoff: boolean;
  boostPickups: readonly StatBoostPickup[];
  demolitions: readonly StatDemolition[];
  ballTouches: readonly StatBallTouch[];
  goal: GoalEvent | null;
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * The net the ball is currently on course to enter, or null.
 *
 * Returns a TEAM: the net at +Y belongs to orange (team 1), the net at -Y to
 * blue (team 0), matching `GOAL_CENTER` and the world's own goal detection. A
 * ball on target for net T scores for the other team.
 */
export const ballTargetNet = (position: Vec3, velocity: Vec3): 0 | 1 | null => {
  if (Math.abs(velocity.y) < 1e-6) return null;

  const net: 0 | 1 = velocity.y > 0 ? 1 : 0;
  const planeY = velocity.y > 0 ? GOAL.scoreThresholdY : -GOAL.scoreThresholdY;
  const t = (planeY - position.y) / velocity.y;
  if (t <= 0 || t > ON_TARGET_HORIZON) return null;

  const x = position.x + velocity.x * t;
  if (Math.abs(x) > GOAL.centerToPost - BALL_RADIUS) return null;

  // Clamped at the floor: a rolling ball is not in free fall, and an unclamped
  // parabola would reject every ground shot from beyond ~0.5 s out.
  const z = Math.max(BALL_RADIUS, position.z + velocity.z * t - 0.5 * GRAVITY * t * t);
  return z <= GOAL.height - BALL_RADIUS ? net : null;
};

// ---------------------------------------------------------------------------
// Empty stats
// ---------------------------------------------------------------------------

/**
 * The zeroed block, and the sum of two.
 *
 * They live HERE rather than beside the profile because this is the file that
 * decides what each field means; ./profile re-exports them as `emptyStats` and
 * `addStats`, which are the names the cosmetics API was specified with. One
 * literal, so a field added to `MatchStats` is zeroed in exactly one place.
 */
export const emptyMatchStats = (): MatchStats => ({
  matches: 0,
  wins: 0,
  goals: 0,
  assists: 0,
  saves: 0,
  shots: 0,
  demolitions: 0,
  mvps: 0,
  ownGoals: 0,
  aerialGoals: 0,
  backwardsGoals: 0,
  longGoals: 0,
  distance: 0,
  secondsPlayed: 0,
  touches: 0,
  boostCollected: 0,
});

/** Sum of two stat blocks, field by field. */
export const mergeMatchStats = (a: MatchStats, b: MatchStats): MatchStats => {
  const out = emptyMatchStats();
  for (const key of Object.keys(out) as (keyof MatchStats)[]) out[key] = a[key] + b[key];
  return out;
};

/** Rocket League's match score, for the MVP and for a post-match panel. */
export const matchScore = (stats: MatchStats): number =>
  stats.goals * MVP_SCORE.goal +
  stats.assists * MVP_SCORE.assist +
  stats.saves * MVP_SCORE.save +
  stats.shots * MVP_SCORE.shot +
  stats.demolitions * MVP_SCORE.demolition;

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

/** One entry in the rolling touch history, for assists and goal attribution. */
interface TouchRecord {
  carIndex: number;
  team: 0 | 1;
  tick: number;
  /** Car centre when it touched. */
  carZ: number;
  onGround: boolean;
  /** Ball position after the contact resolved — where the shot came from. */
  ballPosition: Vec3;
  /** dot(car forward, ball velocity after the contact). Negative is backwards. */
  forwardDotBall: number;
}

/** A live shot, from the tick it was struck until it is saved, scored or lost. */
interface Threat {
  /** Car credited with the shot. */
  by: number;
  /** That car's team. */
  team: 0 | 1;
  /** The net it is on course for. */
  net: 0 | 1;
}

/**
 * Accumulates a `MatchStats` per car from the world's own tick events.
 *
 * Fed AFTER the step, so the ball and car states it reads are the ones the
 * tick produced — which is exactly what the shot and save predicates need,
 * since both judge the velocity a contact left behind.
 */
export class MatchStatsCollector {
  /** Per-car totals. Index is parallel to the car list handed to the ctor. */
  private readonly stats: MatchStats[];
  /** Previous tick's car positions, for the distance accumulator. */
  private readonly lastPositions: Vec3[];
  private readonly teams: (0 | 1)[];

  private readonly touches: TouchRecord[] = [];
  private threat: Threat | null = null;
  private tick = 0;
  private seconds = 0;
  private finished = false;

  constructor(private readonly cars: readonly CarState[]) {
    this.stats = cars.map(() => emptyMatchStats());
    this.lastPositions = cars.map(c => ({ ...c.position }));
    this.teams = cars.map(c => c.team);
  }

  /** Seconds of live play observed so far. */
  get secondsPlayed(): number {
    return this.seconds;
  }

  /**
   * Fold one world tick in.
   *
   * `dt` is the tick length; frozen ticks contribute nothing at all, which is
   * what keeps a nine-second goal celebration out of both the clock and the
   * distance total.
   */
  observe(events: StatTickEvents, ball: BallState, dt: number): void {
    this.tick++;

    if (events.kickoff) {
      // Nothing survives a kickoff: the ball is back at centre and any shot in
      // flight was resolved by whatever caused the reset.
      this.threat = null;
      this.touches.length = 0;
      this.syncPositions();
    }
    if (events.frozen) {
      // The pose is held, so a distance sample would be zero anyway — but
      // re-syncing is what stops the teleport back to the spawns being counted
      // as several thousand uu driven.
      this.syncPositions();
      return;
    }

    this.seconds += dt;
    this.accumulateDistance();

    for (const pickup of events.boostPickups) {
      const index = this.indexOf(pickup.car);
      if (index >= 0) this.stats[index].boostCollected++;
    }
    for (const demo of events.demolitions) {
      const index = this.indexOf(demo.attacker);
      if (index >= 0) this.stats[index].demolitions++;
    }

    const netNow = ballTargetNet(ball.position, ball.velocity);
    const saver = this.recordTouches(events.ballTouches, ball);
    this.resolveThreat(netNow, saver);
    this.openThreat(events.ballTouches, ball, netNow);

    if (events.goal) this.recordGoal(events.goal);
  }

  /**
   * Close the match: stamp `matches`, `wins` and the MVP.
   *
   * `winner` is null for a draw, which has no MVP — the real game does not
   * award one either.
   */
  finish(winner: 0 | 1 | null): void {
    if (this.finished) return;
    this.finished = true;

    for (let i = 0; i < this.stats.length; i++) {
      this.stats[i].matches = 1;
      this.stats[i].wins = winner !== null && this.teams[i] === winner ? 1 : 0;
      this.stats[i].secondsPlayed = this.seconds;
    }
    const mvp = this.mvpIndex(winner);
    if (mvp !== null) this.stats[mvp].mvps = 1;
  }

  /** The winning team's highest scorer, or null on a draw. */
  mvpIndex(winner: 0 | 1 | null): number | null {
    if (winner === null) return null;
    let best: number | null = null;
    let bestScore = -1;
    for (let i = 0; i < this.stats.length; i++) {
      if (this.teams[i] !== winner) continue;
      const score = matchScore(this.stats[i]);
      // Strictly greater, so a tie goes to the lower car index rather than
      // depending on the iteration order of anything.
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    return best;
  }

  /** One car's totals. The returned object is the live one; copy before storing. */
  statsFor(carIndex: number): MatchStats {
    return this.stats[carIndex] ?? emptyMatchStats();
  }

  /** Every car's totals, parallel to the car list. */
  all(): readonly MatchStats[] {
    return this.stats;
  }

  // -------------------------------------------------------------------------

  private indexOf(car: CarState): number {
    return this.cars.indexOf(car);
  }

  private syncPositions(): void {
    for (let i = 0; i < this.cars.length; i++) {
      this.lastPositions[i].x = this.cars[i].position.x;
      this.lastPositions[i].y = this.cars[i].position.y;
      this.lastPositions[i].z = this.cars[i].position.z;
    }
  }

  private accumulateDistance(): void {
    for (let i = 0; i < this.cars.length; i++) {
      const car = this.cars[i];
      // A demolished car is not on the pitch; a respawn is a teleport and must
      // not be charged as distance driven.
      if (!car.demolished) this.stats[i].distance += dist(this.lastPositions[i], car.position);
      this.lastPositions[i].x = car.position.x;
      this.lastPositions[i].y = car.position.y;
      this.lastPositions[i].z = car.position.z;
    }
  }

  /**
   * Fold this tick's contacts into the history and the touch counter, and
   * report whichever defender could be about to be credited with a save.
   */
  private recordTouches(touches: readonly StatBallTouch[], ball: BallState): number | null {
    let saver: number | null = null;
    const threat = this.threat;

    for (const touch of touches) {
      const car = this.cars[touch.carIndex];
      if (!car) continue;
      this.stats[touch.carIndex].touches++;

      // A defender of the threatened net, and not the shooter's own side.
      if (threat !== null && touch.team === threat.net && touch.team !== threat.team) {
        saver = touch.carIndex;
      }

      this.touches.push({
        carIndex: touch.carIndex,
        team: touch.team,
        tick: this.tick,
        carZ: car.position.z,
        onGround: car.onGround,
        ballPosition: { ...ball.position },
        forwardDotBall: dot(forward(car.orientation), ball.velocity),
      });
    }
    // Bounded: only the last two touchers can ever matter to an assist, but a
    // few more are kept so the window test has something to reject.
    if (this.touches.length > 16) this.touches.splice(0, this.touches.length - 16);
    return saver;
  }

  /** Close a live shot: saved by `saver`, or simply gone off target. */
  private resolveThreat(netNow: 0 | 1 | null, saver: number | null): void {
    const threat = this.threat;
    if (threat === null) return;
    if (netNow === threat.net) return;

    if (saver !== null) this.stats[saver].saves++;
    this.threat = null;
  }

  /** Open a shot, if the last contact of the tick put the ball on target. */
  private openThreat(
    touches: readonly StatBallTouch[],
    ball: BallState,
    netNow: 0 | 1 | null
  ): void {
    if (touches.length === 0 || netNow === null) return;

    // The LAST contact of the tick owns the outgoing velocity; an earlier one in
    // the same tick has already been overwritten by it.
    const last = touches[touches.length - 1];
    if (last.team === netNow) return; // aimed at their own net: not a shot
    const speed = Math.hypot(ball.velocity.x, ball.velocity.y, ball.velocity.z);
    if (speed < SHOT_MIN_SPEED) return;

    this.stats[last.carIndex].shots++;
    this.threat = { by: last.carIndex, team: last.team, net: netNow };
  }

  /**
   * Attribute a goal: the scorer or the own-goal, the assist, and the three
   * flavour flags, all read off the touch that produced it.
   */
  private recordGoal(goal: GoalEvent): void {
    this.threat = null;
    const last = this.touches[this.touches.length - 1] ?? null;

    if (goal.scorer === null) {
      // The world reports no scorer exactly when the last toucher defends the
      // net it went into. Nobody else can be charged with it.
      if (last !== null) this.stats[last.carIndex].ownGoals++;
      return;
    }

    const scorer = goal.scorer;
    const stats = this.stats[scorer];
    if (!stats) return;
    stats.goals++;

    if (last !== null && last.carIndex === scorer) {
      // The net the ball entered is the one the CONCEDING team defends.
      const net: 0 | 1 = goal.team === 0 ? 1 : 0;
      const lineY = net === 1 ? GOAL.mouthY : -GOAL.mouthY;

      if (!last.onGround && last.carZ >= AERIAL_MIN_Z) stats.aerialGoals++;
      if (last.forwardDotBall < 0) stats.backwardsGoals++;
      if (Math.abs(lineY - last.ballPosition.y) >= LONG_GOAL_DISTANCE) stats.longGoals++;
    }

    const assist = this.assistFor(scorer, goal.team);
    if (assist !== null) this.stats[assist].assists++;
  }

  /**
   * The team-mate who set it up, or null.
   *
   * Walks back to the most recent touch by anybody but the scorer and accepts
   * it only if it is a team-mate inside the window. Stopping at the first other
   * car — rather than searching for the nearest team-mate — is what makes an
   * opponent's intervening touch cancel the assist instead of being skipped.
   */
  private assistFor(scorer: number, team: 0 | 1): number | null {
    for (let i = this.touches.length - 1; i >= 0; i--) {
      const touch = this.touches[i];
      if (touch.carIndex === scorer) continue;
      if (touch.team !== team) return null;
      if (this.tick - touch.tick > ASSIST_WINDOW_TICKS) return null;
      return touch.carIndex;
    }
    return null;
  }
}
